// Microbe RTS — rival macro-AI.
//
// Difficulty scales the bot (BOT[difficulty] + BOT_BEHAVIOR). The bot harvests, expands,
// produces, evolves, and commits waves on the threat clock. It should be beatable-by-economy on
// tidepool and genuinely threatening on abyss (bigger waves, earlier clock, counters + focus-fire).
//
// SIDE-PARAMETERIZED: tickBot drives an arbitrary `side` (default caller uses 'bot' vs 'you').
// The sandbox drives BOTH sides with this same brain to run bot-vs-bot matches. A bot never touches
// its enemy's state except by pushing its own units toward the enemy's nodes/home. Each bot carries
// its own threat clock in BotMemory so two bots never share one timer slot.

import type { GameState, Side, Unit, WarriorClass, WorldNode, Vec } from '../types.js';
import {
  CLOCKS, COUNTERS, NODE_SPECS, SPECIATION_GATES, MUTATIONS, CAPTURE, SEED_COST, WORLD, TURRET,
} from '../config.js';
import type { BotProfile } from '../config.js';
import type { SimCtx } from './world.js';
import { nodeById, dist } from './world.js';
import { turretsUnlocked, turretCap, placeTurretAt, repairTurrets, repairCostFor } from './turrets.js';
import { photoMult, hasToxinResist, isUnlocked, queueUnit } from './units.js';
import type { ProdQueue } from './units.js';

export interface BotMemory {
  expandTimer: number;
  threatTimer: number; // seconds until this bot's next scheduled push (per-bot; was state.clocks.botThreatTimer)
  wave: number;        // pushes this bot has launched (per-bot; was state.clocks.botWave)
  established: boolean; // has it finished its opening land-grab at least once? once true, a colony lost
                        // in a later fight is REINFORCED (standing army), not re-hoarded for on a reseed.
}
export function createBotMemory(prof: BotProfile): BotMemory {
  return {
    expandTimer: prof.expand.everySeconds,
    threatTimer: CLOCKS.botFirstPushAt + prof.threatClockOffset, // matches world.ts single-bot init
    wave: 0,
    established: false,
  };
}

export interface BotDeps {
  q: ProdQueue;
  mem: BotMemory;
  evolve: (side: Side, mutationId: string) => boolean;
  offered: (side: Side) => string[];
}

/** Each bot's threat clock lives in its own BotMemory (moved off the shared clocks slot so two bots
 *  keep independent schedules). tickBot reads/resets it; sim.ts decrements it at the clocks phase. */
export function tickBot(
  state: GameState,
  ctx: SimCtx,
  side: Side,
  enemy: Side,
  prof: BotProfile,
  deps: BotDeps,
  dt: number,
): void {
  const col = state.colonies[side];
  const home = nodeById(state, col.homeId);

  // OPENING: no home yet — march the dropped squad to the nearest neutral home crater and
  // colonize it. Nothing else can happen until the colony is founded.
  if (!home) {
    foundHome(state, side);
    return;
  }

  // 1. Threat clock -> commit a wave. If the army is too small to commit, don't burn the whole
  //    interval — recheck soon so the first real punch lands as soon as a force exists.
  //    (The timer is DECREMENTED at the clocks phase in sim.ts, preserving the original
  //    check-then-decrement ordering; here we only read + reset it.)
  if (deps.mem.threatTimer <= 0) {
    const launched = launchWave(state, side, enemy, prof.waveSizeMult, prof.focusFires, prof.minArmyToPush);
    if (launched) {
      deps.mem.wave++;
      deps.mem.threatTimer = prof.botPushInterval; // gene: midgame wave cadence (was CLOCKS.botPushInterval)
    } else {
      deps.mem.threatTimer = CLOCKS.botPushRetry;
    }
  }

  // 2. Evolve (spend the big lumps): speciate, commit a lineage by territory, then deepen its chain.
  //    BUT only once the reachable land goal is SECURED (mem.established). Sandbox read (Ara): the
  //    expansionist trap is teching early — buying somatics + speciation gates out of the same bank
  //    that should be paying for the next colony. The real opening is territory FIRST (farm speed),
  //    which then funds evolution + a heavier army LATER. During the land-grab we spend zero lumps on
  //    tech. This never deadlocks: when no reachable land is left, landTarget collapses to what we
  //    hold, macroBuild flips established, and tech comes online (see macroBuild's established gate).
  if (deps.mem.established) tryEvolve(state, side, deps, prof);

  // 3. Produce (self-throttles on affordability).
  macroBuild(state, ctx, side, prof, deps.q, deps.mem);

  // 3.6 Defensive turrets (LOW priority): only once established + unlocked, and only with a comfortable
  //     surplus so it never competes with expansion, waves, or tech. Build up to cap, then repair any
  //     damaged/destroyed ones (else dead rubble would permanently occupy a cap slot).
  if (deps.mem.established) {
    tryBuildTurrets(state, side, home);
    tryRepairTurrets(state, side);
  }

  // 3.5 The amino wall: the next colony costs 70 amino and Home only trickles ~0.2/s. Rather than
  //     sit and wait, send the Queen to HUNT grazers — a far faster amino source. Self-limiting: she
  //     only hunts while actually amino-blocked toward a reachable next node (see huntGrazers).
  huntGrazers(state, side, prof);

  // 4. Expand: dispatch drones to seed forward colonies on a cadence. Cadence + how many drones go
  //    out at once come from the profile — a land-grab profile fires often and in parallel.
  deps.mem.expandTimer -= dt;
  if (deps.mem.expandTimer <= 0) {
    deps.mem.expandTimer = prof.expand.everySeconds;
    expand(state, side, prof.expand.parallelSeeds);
  }
}

// ---------------------------------------------------------------------------
// Opening: find + colonize a home crater with the dropped squad.
// ---------------------------------------------------------------------------
function foundHome(state: GameState, side: Side): void {
  const squad = state.units.filter((u) => u.side === side && u.hp > 0);
  if (!squad.length) return;
  let cx = 0;
  let cy = 0;
  for (const u of squad) { cx += u.pos.x; cy += u.pos.y; }
  cx /= squad.length;
  cy /= squad.length;

  // Nearest still-neutral crater to the squad — whatever it settles first BECOMES its home now, so
  // it heads for the closest crater it can actually exploit (falls back to the nearest crater of any
  // type if nothing exploitable is teched-in yet — reef/vent always are).
  let target: WorldNode | null = null;
  let bestD = Infinity;
  for (const n of state.nodes) {
    if (n.owner !== 'neutral' || n.type === 'home') continue;
    if (!canExploit(state, side, n)) continue;
    const d = dist({ x: cx, y: cy }, n.pos);
    if (d < bestD) { bestD = d; target = n; }
  }
  if (!target) {
    for (const n of state.nodes) {
      if (n.owner !== 'neutral' || n.type === 'home') continue;
      const d = dist({ x: cx, y: cy }, n.pos);
      if (d < bestD) { bestD = d; target = n; }
    }
  }
  if (!target) return;

  // The lone opening drone marches onto the core to channel the (free) Home seed.
  for (const u of squad) {
    const tgt = target.pos;
    if (dist(u.pos, tgt) > 40) u.moveTarget = { x: tgt.x, y: tgt.y };
  }
}

// ---------------------------------------------------------------------------
// Grazer hunting — the early-game amino engine.
// ---------------------------------------------------------------------------
// (Queen hunt radius is now the per-profile gene prof.huntRadius — see BotTune in config.ts.)

/** Nearest ALIVE wild grazer to `from` within `maxDist` (null if none in range). */
function nearestGrazer(state: GameState, from: Vec, maxDist: number): Unit | null {
  let best: Unit | null = null;
  let bestD = maxDist;
  for (const u of state.units) {
    if (u.side !== 'wild' || u.kind !== 'grazer' || u.hp <= 0) continue;
    const d = dist(from, u.pos);
    if (d < bestD) { bestD = d; best = u; }
  }
  return best;
}

/**
 * Send the Queen to hunt grazers for amino — the fast alternative to waiting on the Home trickle to
 * fund the next colony. Deliberately NOT myopic:
 *   • NEED — only fires while the colony is amino-blocked toward a next colony it can actually reach
 *     (a neutral, exploitable crater still exists). The instant it can afford a seed, she stops.
 *   • AFFORDABLE — only the Queen goes (she solos one grazer, per QUEEN tuning), so the warrior army
 *     is never stripped for defense/waves. A badly-hurt Queen is pulled off to mend.
 *   • REACH / SCOUTING — grazer positions may be fogged, so if none is within HUNT_RADIUS she SWEEPS
 *     toward the wild mid-band to FIND one (her travel doubles as scouting), rather than no-op-waiting.
 * To actually farm a grazer a colony unit must FOCUS it (combat.ts only farms an explicitly-focused
 * neutral), so we set both focusId (to bite + credit amino) and moveTarget (to approach/track it).
 */
function huntGrazers(state: GameState, side: Side, prof: BotProfile): void {
  const col = state.colonies[side];
  const home = nodeById(state, col.homeId);
  if (!home) return;
  // Once we can pay for the next seed, stop hunting — release the Queen from any prey she's locked on.
  if (col.res.amino >= SEED_COST.amino) {
    const q = findQueen(state, side);
    if (q && q.focusId) q.focusId = null;
    return;
  }
  // Only bother if there's still a reachable colony to fund (a neutral, uncontested, exploitable node).
  const wantsLand = state.nodes.some(
    (n) => n.owner === 'neutral' && n.type !== 'home' && !n.contested && canExploit(state, side, n),
  );
  if (!wantsLand) return;

  const queen = findQueen(state, side);
  if (!queen) return;
  // Don't send a badly-hurt Queen to her death — physically peel her OFF the grazer and march her
  // home (an explicit moveTarget outranks foe-chase), where her regen can recover her. Just nulling
  // focus wasn't enough: she'd keep auto-retaliating the point-blank grazer and die on the spot.
  if (queen.hp < queen.maxHp * prof.queenHuntRetreatFrac) {
    queen.focusId = null;
    queen.bandStance = 'hold';
    queen.moveTarget = { x: home.pos.x, y: home.pos.y };
    return;
  }

  // Keep tracking a live prey she's already locked onto; otherwise pick the nearest in range.
  const locked = queen.focusId
    ? state.units.find((u) => u.id === queen.focusId && u.side === 'wild' && u.hp > 0) ?? null
    : null;
  const prey = locked ?? nearestGrazer(state, queen.pos, prof.huntRadius);
  if (!prey) {
    // None known/in-range — sweep toward the wild mid-band to discover one (scouting IS the hunt).
    if (!queen.moveTarget) queen.moveTarget = { x: WORLD.width / 2, y: WORLD.height * 0.5 };
    return;
  }
  queen.focusId = prey.id;
  queen.bandStance = 'aggressive';
  queen.moveTarget = { x: prey.pos.x, y: prey.pos.y };
}

/** This side's Queen (a crowned drone), if she's alive. */
function findQueen(state: GameState, side: Side): Unit | null {
  for (const u of state.units) {
    if (u.side === side && u.kind === 'drone' && u.queen && u.hp > 0) return u;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Evolution.
// ---------------------------------------------------------------------------
/** The bot's committed lineage keystone id (the Tier-II pick that gates its chain), if any. */
function committedKeystone(state: GameState, side: Side): string | null {
  for (const kid of state.colonies[side].keystones) {
    const m = MUTATIONS.find((x) => x.id === kid);
    if (m?.lineage) return kid;
  }
  return null;
}
/** How well the bot's TERRITORY supports a lineage — drives which keystone it commits to, so two
 *  bots on different halves of the map diverge into different species instead of mirroring. */
function lineageFit(state: GameState, side: Side, lineage: string): number {
  const light = state.nodes.filter((n) => n.owner === side && n.type === 'light').length;
  const vent = state.nodes.filter((n) => n.owner === side && n.type === 'vent').length;
  const toxin = state.nodes.filter((n) => n.owner === side && n.type === 'toxin').length;
  if (lineage === 'phototroph') return light * 2; // light shelves want photosynthesis
  if (lineage === 'chemotroph') return vent * 2 + toxin * 2; // vents/seeps want chemosynthesis
  if (lineage === 'phagotroph') return 1; // no econ node needed — the aggro default when nothing leans econ
  return 0;
}
function tryEvolve(state: GameState, side: Side, deps: BotDeps, prof: BotProfile): void {
  const offered = deps.offered(side);
  if (!offered.length) return;
  const committed = committedKeystone(state, side);
  // Score each offered node. Speciation climbs tiers first; among keystones the bot COMMITS to the
  // lineage its board supports (or a matching Tier-IV capstone); then it VALUES its lineage-chain
  // nodes (requires-gated — rank 1 in the old code, so they never got bought) above generic somatics.
  const score = (id: string): number => {
    const m = MUTATIONS.find((x) => x.id === id);
    if (!m) return 0;
    if (m.effect?.unlockTurrets) return 20; // modest: above generic somatic (10), below spec/keystones
    if (m.type === 'speciation') return prof.speciationScore; // gene: rush tiers (100) vs linger (<60)
    if (m.keystone) {
      if (m.lineage) {
        // A Tier-IV capstone (requires spec_t4) is the payoff — take it the instant it's offered.
        const capstone = m.requires?.includes('spec_t4') ? 40 : 0;
        return 60 + capstone + lineageFit(state, side, m.lineage);
      }
      return prof.aerobicKeystoneScore; // gene: the oxygen bet (Aerobic) — off-lineage keystone value
    }
    // Non-keystone somatic. A node gated behind the committed lineage keystone is on-lineage → prize it.
    if (committed && m.requires?.includes(committed)) return 30;
    if (m.category === 'economy') return prof.econSomaticScore; // gene: economy vs combat somatic bias (default 10 == combat 10)
    return 10;
  };
  let bestId: string | null = null;
  let bestScore = -1;
  for (const id of offered) {
    const s = score(id);
    if (s > bestScore) {
      bestScore = s;
      bestId = id;
    }
  }
  if (bestId) deps.evolve(side, bestId);
}

// ---------------------------------------------------------------------------
// Production.
// ---------------------------------------------------------------------------
/** Fresh seeder drones — excludes Queens (kind stays 'drone' after a seed, but a Queen is bound to
 *  her colony and no longer a free seeder). This is what the expansion logic counts. */
function countSeeders(state: GameState, side: Side): number {
  let c = 0;
  for (const u of state.units) if (u.side === side && u.kind === 'drone' && !u.queen && u.hp > 0) c++;
  return c;
}

function macroBuild(state: GameState, ctx: SimCtx, side: Side, prof: BotProfile, q: ProdQueue, mem: BotMemory): void {
  const col = state.colonies[side];
  const home = nodeById(state, col.homeId);
  if (!home) return;

  const ex = prof.expand;
  // Seeders counts BOTH built drones and any already in the production queue — macroBuild runs every
  // tick, and queueUnit pays + enqueues with no dedup, so without counting in-flight builds the bot
  // would spend its whole bank on drones (3 at once from the 30 opening amino) instead of saving it.
  const scoutsQueued = q.units.reduce((c, j) => c + (j.side === side && j.kind === 'drone' ? 1 : 0), 0);
  const seeders = countSeeders(state, side) + scoutsQueued;
  const capturableHeld = state.nodes.filter((n) => n.owner === side && n.type !== 'home').length;

  // Clamp the land goal to what's ACTUALLY reachable right now: nodes already held + neutral craters
  // this colony can currently exploit (photosynthesis/toxin-resist gate un-teched nodes out). Without
  // this, a profile whose targetNodes exceeds the seedable count (e.g. it needs tech it hasn't taken,
  // or the enemy holds the rest) would sit in the expansion branch forever — capping its army below
  // minArmyToPush and never attacking: a permanent turtle. The goal rises again as the bot techs into
  // more node types. This makes any profile's targetNodes safe (it means "grab up to N if reachable").
  const exploitableNeutral = state.nodes.filter(
    (n) => n.owner === 'neutral' && n.type !== 'home' && !n.contested && canExploit(state, side, n),
  ).length;
  const landTarget = Math.min(ex.targetNodes, capturableHeld + exploitableNeutral);

  // EXPANSION FIRST (anti-turtle): while the bot still wants land, it does exactly one thing —
  // get seeder drones out and BANK income toward SEED_COST so it can actually pay for the next
  // colony. Early nodes ~quadruple amino income, so landing them fast snowballs everything. The old
  // bot frittered its amino on extra drones + warriors and never crossed the seed cost — it turtled.
  if (capturableHeld < landTarget) {
    // The FIRST colony is the amino wall: home trickles ~0.2 amino/s and a seed costs 70, so the
    // bot must bank almost purely to land node #1 — one seeder, no warriors, everything else saved.
    // Once a node is held, amino income ~quintuples and it ramps to parallel seeding + token defense.
    // Pure-bank only on the GENUINE opening — an ESTABLISHED bot shoved back to 0 colonies must keep
    // reinforcing (armyFloor stays standingArmyFloor below), not sit defenseless hoarding for a reseed.
    const firstColony = capturableHeld === 0 && !mem.established;
    const wantSeeders = firstColony ? 1 : ex.parallelSeeds;

    // 1. Make sure enough fresh seeders exist to pre-position on the (parallel) targets.
    if (seeders < wantSeeders) {
      queueUnit(state, q, side, home.id, 'drone');
      return;
    }
    // 2. Bank toward SEED_COST while keeping a defensive army. During the OPENING land-grab that
    //    army floor is tiny (earlyArmyFloor) so we don't starve the seed of amino; but once the bot
    //    has established (finished its first land-grab), a colony lost in a later fight drops it back
    //    here — and then it must keep REINFORCING to the standing floor, not wither to 2 while it
    //    hoards for a reseed. firstColony banks purely (build nothing).
    const armyFloor = firstColony ? 0 : (mem.established ? prof.standingArmyFloor : prof.earlyArmyFloor);
    if (armySize(state, side) < armyFloor) {
      queueUnit(state, q, side, home.id, pickWarrior(state, ctx, side, prof));
    }
    // Build nothing else: let energy + amino climb to SEED_COST so expand()'s pre-positioned drone
    // (which is already sitting on the crater, funded, waiting) completes the seed the instant we pay.
    return;
  }
  mem.established = true; // reached the reachable land goal at least once — now defend it as a colony

  // Land target met — normal macro. Keep seeder drones on hand for the odd forward colony + vision.
  if (seeders < ex.desiredScouts && ctx.rng() < ex.scoutRatio) {
    queueUnit(state, q, side, home.id, 'drone');
    return;
  }

  // Keep a speciation fund so the bot can still tech up.
  const canStillSpec = col.tier < 4;
  const nextGate = canStillSpec
    ? SPECIATION_GATES[(col.tier + 1) as 2 | 3 | 4]
    : undefined;
  const reserve = nextGate ? nextGate.energy * prof.specReserveFrac : 0; // gene: bank-toward-tech intensity
  if (canStillSpec && col.res.energy < reserve) {
    // Even while banking for tech, maintain a minimum standing army so the bot always has a force
    // to commit — this is what stops it from turtling on pure economy for 15+ minutes.
    if (armySize(state, side) < prof.standingArmyFloor) {
      queueUnit(state, q, side, home.id, pickWarrior(state, ctx, side, prof));
    } else if (seeders < ex.desiredScouts) {
      queueUnit(state, q, side, home.id, 'drone');
    }
    return;
  }

  const kind = pickWarrior(state, ctx, side, prof);
  queueUnit(state, q, side, home.id, kind);
}

/** The bot's committable army: combat units that aren't drones (kept back to seed) or healers. */
function armySize(state: GameState, side: Side): number {
  let c = 0;
  for (const u of state.units) {
    if (u.side === side && u.hp > 0 && u.kind !== 'drone' && u.kind !== 'healer') c++;
  }
  return c;
}

/** Weighted class pick — draws EXACTLY ONE ctx.rng() per call (determinism-critical). With all
 *  classWeights equal (the default), total = cs.length and this returns cs[floor(rng*len)] over the
 *  list IN ORDER — byte-identical to the former uniform `cs[floor(rng*len)]`. A zeroed total falls
 *  back to that same uniform draw so a GA-zeroed comp can't divide-by-zero or desync the RNG stream. */
function weightedPick(cs: WarriorClass[], prof: BotProfile, ctx: SimCtx): WarriorClass {
  const w = (k: WarriorClass): number => (prof.classWeights as Record<string, number>)[k] ?? 1;
  let total = 0;
  for (const k of cs) total += w(k);
  if (total <= 0) return cs[Math.floor(ctx.rng() * cs.length)];
  let r = ctx.rng() * total;
  for (const k of cs) { r -= w(k); if (r < 0) return k; }
  return cs[cs.length - 1];
}

function pickWarrior(state: GameState, ctx: SimCtx, side: Side, prof: BotProfile): WarriorClass {
  const col = state.colonies[side];
  const enemy: Side = side === 'bot' ? 'you' : 'bot';
  const roster: WarriorClass[] = (['lancer', 'stinger', 'phalanx', 'engulfer', 'toxifer'] as WarriorClass[])
    .filter((k) => isUnlocked(col, k));
  if (!roster.length) return 'lancer';

  if (prof.respectsCounters) {
    // Find the enemy's dominant warrior kind, pick a class STRONG against it.
    const tally = new Map<WarriorClass, number>();
    for (const u of state.units) {
      if (u.side !== enemy || u.kind === 'drone' || u.kind === 'healer' || u.hp <= 0) continue;
      tally.set(u.kind as WarriorClass, (tally.get(u.kind as WarriorClass) ?? 0) + 1);
    }
    let dom: WarriorClass | null = null;
    let domN = 0;
    for (const [k, n] of tally) if (n > domN) { domN = n; dom = k; }
    if (dom) {
      const counters = roster.filter((k) => COUNTERS[k].includes(dom as WarriorClass));
      if (counters.length) return weightedPick(counters, prof, ctx); // gene: army-comp bias within counters
    }
  }
  return weightedPick(roster, prof, ctx); // gene: army-comp bias (classWeights); uniform at default
}

// ---------------------------------------------------------------------------
// Waves.
// ---------------------------------------------------------------------------
function launchWave(state: GameState, side: Side, enemy: Side, waveSizeMult: number, focusFires: boolean, minArmyToPush: number): boolean {
  // Commit the fighting army; keep drones (seeders/vision) and healers out of the charge.
  const warriors = state.units.filter(
    (u) => u.side === side && u.kind !== 'drone' && u.kind !== 'healer' && u.hp > 0,
  );
  if (warriors.length < minArmyToPush) return false; // gene: hold-vs-push threshold

  const commit = Math.max(minArmyToPush, Math.floor(warriors.length * waveSizeMult));
  const wave = warriors.slice(0, Math.min(warriors.length, commit));

  const target = pickPlayerTarget(state, side, enemy);
  if (!target) return false;

  for (const w of wave) {
    w.moveTarget = { x: target.x, y: target.y };
    w.bandStance = 'aggressive';
    w.focusId = null;
  }
  if (focusFires) {
    const foe = nearestEnemyTo(state, enemy, target);
    if (foe) for (const w of wave) w.focusId = foe.id;
  }

  // Tell the side being attacked it's incoming (drives their UI toast when that side is the human).
  state.events.push({ kind: 'underAttack', side: enemy, pos: { x: target.x, y: target.y }, at: state.clocks.sim });
  if (state.events.length > 64) state.events.splice(0, state.events.length - 64);
  return true;
}

function pickPlayerTarget(state: GameState, side: Side, enemy: Side): Vec | null {
  const myHome = nodeById(state, state.colonies[side].homeId);
  const origin = myHome ? myHome.pos : { x: 0, y: 0 };
  // Prefer the enemy's nearest capturable node; fall back to their Home.
  let best: WorldNode | null = null;
  let bestD = Infinity;
  for (const n of state.nodes) {
    if (n.owner !== enemy) continue;
    const d = dist(origin, n.pos);
    if (d < bestD) { bestD = d; best = n; }
  }
  if (best) return { x: best.pos.x, y: best.pos.y };
  const foeHome = nodeById(state, state.colonies[enemy].homeId);
  return foeHome ? { x: foeHome.pos.x, y: foeHome.pos.y } : null;
}

function nearestEnemyTo(state: GameState, side: Side, pos: Vec): Unit | null {
  let best: Unit | null = null;
  let bestD = Infinity;
  for (const u of state.units) {
    if (u.side !== side || u.hp <= 0) continue;
    const d = dist(pos, u.pos);
    if (d < bestD) { bestD = d; best = u; }
  }
  return best;
}

// ---------------------------------------------------------------------------
// Expansion — a drone seeds forward colonies (auto-worked once owned).
// ---------------------------------------------------------------------------
/** A node worth owning: its yield isn't gated behind tech the bot hasn't teched into. */
function canExploit(state: GameState, side: Side, node: WorldNode): boolean {
  const col = state.colonies[side];
  const spec = NODE_SPECS[node.type];
  if (spec.needsPhoto && photoMult(col) <= 0) return false;
  if (spec.requiresResist && !hasToxinResist(col)) return false;
  return true;
}

// Low-priority defensive build: once established + unlocked, drop turrets around home up to the cap,
// but only with a comfortable surplus over the turret cost so it never starves seeds/army. Deterministic
// ring placement >= TURRET.minSepDeg apart (placeTurretAt re-validates cap/sep/cost).
function tryBuildTurrets(state: GameState, side: Side, home: WorldNode): void {
  const col = state.colonies[side];
  if (!turretsUnlocked(col)) return;
  const mine = state.turrets.filter((t) => t.side === side);
  if (mine.length >= turretCap(col)) return;
  if (col.res.energy < TURRET.cost.energy + 220 || col.res.amino < TURRET.cost.amino + 60) return; // surplus gate
  const pos = pickTurretPos(home, mine);
  if (pos) placeTurretAt(state, side, pos);
}

// Repair damaged/destroyed turrets when there's a comfortable surplus (so rubble doesn't permanently
// eat a cap slot). Surplus-gated like the build so it never starves the army/expansion.
function tryRepairTurrets(state: GameState, side: Side): void {
  const col = state.colonies[side];
  const cost = repairCostFor(state, side);
  if (cost.energy <= 0) return; // nothing to repair
  if (col.res.energy < cost.energy + 200 || col.res.amino < cost.amino + 50) return; // surplus gate
  repairTurrets(state, side);
}

// First deterministic ring angle (of 12 candidates) that is >= minSepDeg from every existing turret.
function pickTurretPos(home: WorldNode, mine: { pos: Vec }[]): Vec | null {
  const ringR = home.radius * ((TURRET.ringMinFrac + TURRET.ringMaxFrac) / 2);
  const existing = mine.map((t) => Math.atan2(t.pos.y - home.pos.y, t.pos.x - home.pos.x));
  const minSep = (TURRET.minSepDeg * Math.PI) / 180;
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    let ok = true;
    for (const e of existing) {
      let d = Math.abs(a - e) % (Math.PI * 2);
      if (d > Math.PI) d = Math.PI * 2 - d;
      if (d < minSep) { ok = false; break; }
    }
    if (ok) return { x: home.pos.x + Math.cos(a) * ringR, y: home.pos.y + Math.sin(a) * ringR };
  }
  return null;
}

function expand(state: GameState, side: Side, parallelSeeds: number): void {
  const home = nodeById(state, state.colonies[side].homeId);
  if (!home) return;

  // PRE-POSITION seeders: send idle fresh drones to sit ON the target craters even before the colony
  // can pay. The seed channel (updateCapture) holds, funded, and completes the instant resources
  // cross SEED_COST — so there's zero travel delay once the bank fills. This, plus the bank-for-seed
  // reserve in macroBuild, is what turns the old turtle into a bot that actually lands colonies.
  const onNeutralCore = (u: Unit): boolean => state.nodes.some(
    (n) => n.owner === 'neutral' && n.type !== 'home' &&
      dist(u.pos, n.pos) <= n.radius * CAPTURE.coreRadiusFrac,
  );
  const idle: Unit[] = [];
  for (const u of state.units) {
    if (u.side !== side || u.kind !== 'drone' || u.queen || u.hp <= 0) continue;
    if (u.moveTarget) continue;      // already marching to a target
    if (onNeutralCore(u)) continue;  // already sitting on a crater, channeling — leave it
    idle.push(u);
    if (idle.length >= parallelSeeds) break;
  }
  if (!idle.length) return;

  // Candidate neutral, uncontested, exploitable nodes, nearest home first.
  const candidates = state.nodes
    .filter((n) => n.owner === 'neutral' && n.type !== 'home' && !n.contested && canExploit(state, side, n))
    .sort((a, b) => dist(home.pos, a.pos) - dist(home.pos, b.pos));
  if (!candidates.length) return;

  // Assign each idle drone to the nearest still-unclaimed candidate (greedy, distinct targets).
  const taken = new Set<WorldNode>();
  for (const drone of idle) {
    let best: WorldNode | null = null;
    let bestD = Infinity;
    for (const n of candidates) {
      if (taken.has(n)) continue;
      const d = dist(drone.pos, n.pos);
      if (d < bestD) { bestD = d; best = n; }
    }
    if (!best) break; // ran out of distinct targets
    taken.add(best);
    drone.moveTarget = { x: best.pos.x, y: best.pos.y };
  }
}
